import { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Send, Trophy, Euro, Package, Truck, MapPin, Calendar, CheckCircle2,
  ShieldCheck, AlertCircle, Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMyOffers, confirmStructuredOffer, withdrawStructuredOffer } from '@/lib/api';
import { fmt2 } from '@/lib/utils';

const STATUS_BADGE = {
  submitted: { label: 'Gesendet', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  viewed_by_buyer: { label: 'Vom Käufer gesehen', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  accepted: { label: 'Vom Käufer angenommen', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  declined: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  countered: { label: 'Gegenangebot vom Käufer', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  withdrawn: { label: 'Zurückgezogen', cls: 'bg-slate-100 text-slate-500' },
  expired: { label: 'Abgelaufen', cls: 'bg-slate-100 text-slate-500' },
  transaction_created: { label: 'Transaktion erfasst', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};

export default function MyOffersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMyOffers();
      setOffers(res.data.offers || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const handleConfirm = async () => {
    if (!confirmOffer) return;
    setConfirmLoading(true);
    try {
      const res = await confirmStructuredOffer(confirmOffer.offer_id);
      setConfirmResult(res.data);
      toast.success('Deal bestätigt — Transaktion erfasst!');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Bestätigen.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleWithdraw = async (offerId) => {
    if (!window.confirm('Angebot wirklich zurückziehen?')) return;
    try {
      await withdrawStructuredOffer(offerId);
      toast.success('Angebot zurückgezogen.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler.');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-orange-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Anmeldung erforderlich</h2>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/login')}>Anmelden</Button>
        </div>
      </div>
    );
  }

  const accepted = offers.filter((o) => o.status === 'accepted');

  return (
    <div className="min-h-screen bg-background" data-testid="my-offers-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-manrope text-3xl font-bold flex items-center gap-2">
            <Send className="h-7 w-7 text-orange-500" />
            Meine Angebote
          </h1>
          <p className="text-muted-foreground mt-1">
            Übersicht aller Angebote, die Sie auf Gesuche abgegeben haben.
          </p>
        </div>

        {accepted.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 flex items-start gap-3" data-testid="action-required-banner">
            <ShieldCheck className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900 dark:text-amber-200">
                {accepted.length} Angebot(e) warten auf Ihre Bestätigung
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
                Der Käufer hat angenommen. Bitte bestätigen Sie den Deal, damit die Transaktion erfasst wird.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36" />)}
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="my-offers-empty">
            <Send className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">Sie haben noch keine Angebote abgegeben</p>
            <Button className="mt-4 bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/wanted')}>
              Gesuche durchsuchen
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-offers-list">
            {offers.map((o) => {
              const sb = STATUS_BADGE[o.status] || { label: o.status, cls: 'bg-slate-100' };
              return (
                <Card key={o.offer_id} data-testid={`my-offer-${o.offer_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">
                          Gesuch: {o.wanted_ad?.material_type_description || '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">{o.wanted_ad?.category_name}</p>
                      </div>
                      <Badge className={`text-xs ${sb.cls}`} data-testid={`my-offer-status-${o.offer_id}`}>{sb.label}</Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-2">
                      <Metric icon={<Euro className="h-3.5 w-3.5 text-orange-500" />} label="Stückpreis" value={`${fmt2(o.unit_price_eur)} €`} />
                      <Metric icon={<Euro className="h-3.5 w-3.5 text-green-600" />} label="Gesamt" value={<span className="font-bold">{fmt2(o.total_price_eur)} €</span>} />
                      <Metric icon={<Package className="h-3.5 w-3.5 text-blue-500" />} label="Menge" value={`${o.offered_qty} ${o.quantity_unit}`} />
                      <Metric icon={<Truck className="h-3.5 w-3.5 text-purple-500" />} label="Incoterm" value={o.incoterm} />
                      {o.pickup_site && (
                        <Metric icon={<MapPin className="h-3.5 w-3.5 text-emerald-600" />} label="Standort" value={`${o.pickup_site.city || ''} ${o.pickup_site.country || ''}`.trim()} />
                      )}
                      <Metric icon={<Calendar className="h-3.5 w-3.5 text-slate-500" />} label="Gültig bis" value={o.offer_valid_until} />
                    </div>

                    {o.status === 'accepted' && (
                      <div className="mt-3 pt-3 border-t flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 gap-1"
                          onClick={() => { setConfirmOffer(o); setConfirmResult(null); }}
                          data-testid={`confirm-offer-${o.offer_id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Deal bestätigen
                        </Button>
                      </div>
                    )}

                    {(o.status === 'submitted' || o.status === 'viewed_by_buyer') && (
                      <div className="mt-3 pt-3 border-t flex items-center justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleWithdraw(o.offer_id)}
                          data-testid={`withdraw-offer-${o.offer_id}`}
                        >
                          Zurückziehen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm Deal Dialog */}
      <Dialog open={!!confirmOffer} onOpenChange={(o) => { if (!o) { setConfirmOffer(null); setConfirmResult(null); } }}>
        <DialogContent data-testid="confirm-deal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-green-500" />
              Deal bestätigen
            </DialogTitle>
            <DialogDescription>
              Mit Ihrer Bestätigung wird die Transaktion erfasst und die Provision (Käufer) berechnet.
            </DialogDescription>
          </DialogHeader>

          {confirmResult ? (
            <div className="space-y-3">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2 text-sm">
                <p className="font-semibold text-green-800 dark:text-green-300 mb-2">Transaktion erfasst</p>
                <Row label="Transaktionsbetrag" value={`${fmt2(confirmResult.transaction_amount_eur)} €`} />
                <Row label="Provisionsrate" value={`${fmt2(confirmResult.commission_rate_pct)} %`} />
                <Separator />
                <Row label="Provision (Käufer)" value={<span className="font-bold text-orange-600">{fmt2(confirmResult.commission_eur)} €</span>} />
              </div>
              <DialogFooter>
                <Button onClick={() => { setConfirmOffer(null); setConfirmResult(null); }}>Schließen</Button>
              </DialogFooter>
            </div>
          ) : confirmOffer && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 space-y-1.5 text-sm">
                <Row label="Gesuch" value={confirmOffer.wanted_ad?.material_type_description || '—'} />
                <Row label="Menge × Preis" value={`${confirmOffer.offered_qty} ${confirmOffer.quantity_unit} × ${fmt2(confirmOffer.unit_price_eur)} €`} />
                <Row label="Gesamtbetrag" value={<span className="font-bold">{fmt2(confirmOffer.total_price_eur)} €</span>} />
                <Row label="Incoterm" value={confirmOffer.incoterm} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOffer(null)} disabled={confirmLoading}>Abbrechen</Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={handleConfirm} disabled={confirmLoading} data-testid="confirm-deal-submit">
                  {confirmLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Deal bestätigen
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">{icon}<span>{label}</span></div>
      <div className="font-medium text-sm mt-0.5 truncate">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
